import { FIRE_LEVELS, fireDanger, type FireLevel } from "../../../shared/outdoors";
import { useHousehold } from "../../app/useHousehold";
import { dataStateOf } from "../../data/queries";
import { useAlerts, useGrid } from "../../data/weatherQueries";
import { Icon } from "../../ui/Icon";
import { EmptyState } from "../../ui/StatusBadge";
import { useNow } from "../../ui/time";
import { untilText } from "../alerts/alertUi";
import { NeedsLocation, remPx, SkyFoot } from "../sky/common";
import type { WidgetDefinition, WidgetProps } from "../types";
import { WidgetHeader } from "../WidgetHeader";
import "./outdoors.css";

type FireConfig = Record<string, never>;

/** Fire danger near home in plain words: official fire weather alerts first, then humidity, wind and NWS fire indices. */
function FireDanger({ size, fullscreen, openFullscreen }: WidgetProps<FireConfig>) {
  const { settings, timeZone } = useHousehold();
  useNow("minute");
  const alerts = useAlerts();
  const grid = useGrid();
  const state = dataStateOf(grid, 90 * 60_000);
  if (!settings.location) return <NeedsLocation icon="flame" title="Fire danger needs a home location" detail="Set a general location to see fire danger near home." compact={size.variant === "compact"} />;
  if (!grid.data && !alerts.data) return state === "loading" ? <div className="widget-loading" /> : <EmptyState icon="flame" title="Fire danger unavailable" detail={grid.error?.message} />;

  const d = fireDanger(alerts.data?.alerts ?? [], grid.data?.fire ?? null);
  const info = FIRE_LEVELS[d.level];
  const compact = size.height < remPx() * 11.3;

  return (
    <div className={`outdoor fire fire-${d.level}`} style={{ ["--fire" as string]: info.color }}>
      {!compact && <WidgetHeader title="Fire danger" meta="Next 24 hours near home" onTitle={fullscreen ? undefined : openFullscreen} />}
      {d.alerts.map((a) => (
        <div key={a.id} className="fire-alert">
          <Icon name="flame" size="1.1rem" />
          <strong>{a.event}</strong>
          <span>{untilText(a, timeZone, settings.clock24h)}</span>
        </div>
      ))}
      <div className="fire-now">
        <span className="fire-gauge" aria-hidden="true">
          {([1, 2, 3, 4, 5] as FireLevel[]).map((l) => (
            <i key={l} className={l <= d.level ? "on" : ""} />
          ))}
        </span>
        <span className="fire-text">
          <strong>{info.name}</strong>
          {!compact && <span>{info.advice}</span>}
        </span>
      </div>
      {!compact && (
        <ul className="fire-reasons">
          {d.reasons.map((r) => (
            <li key={r}>{r}</li>
          ))}
        </ul>
      )}
      {!compact && <SkyFoot source="NWS fire weather alerts and gridpoint forecast" stamp={grid.data?.stamp} state={state} updatedAt={grid.dataUpdatedAt} />}
    </div>
  );
}

export const fireDangerWidget: WidgetDefinition<FireConfig> = {
  type: "fire-danger",
  version: 1,
  name: "Fire danger",
  description: "Wildfire danger near home in plain words, led by any Red Flag Warning, with humidity, gusts and NWS fire indices behind it.",
  icon: "flame",
  category: "weather",
  defaultSize: { w: 4, h: 4 },
  minSize: { w: 2, h: 2 },
  stackHeight: 250,
  defaults: {},
  settings: [],
  provider: "nws",
  capabilities: { requiresLocation: true },
  supportsFullscreen: true,
  Component: FireDanger,
};
